/**
 * Chile (Liciora, the `cl` deployment): the `es.ts` sentences that are only true
 * in Spain, said for Chile in es-CL, voice «tú». Merged in `i18n/index.ts` over
 * `{ ...es, ...esNoTelegram }` on the cl build only (Chile runs no Telegram bot),
 * so Spain's own catalogue is untouched. Same mechanism as `ru.kz.ts`.
 *
 * What changes, and nothing else (Liciora COMMON brief, fixed decisions):
 *  - money: «$ 1.234.567» (sign first) instead of «€»; pesos, never euros;
 *  - RUT instead of NIF/CIF/NIE, with its dígito verificador;
 *  - Mercado Público (ChileCompra) instead of PLACSP; bases (administrativas /
 *    técnicas) instead of pliegos; organismo comprador instead of órgano de
 *    contratación; regiones instead of comunidades autónomas; UNSPSC instead of
 *    CPV; monto instead of importe; oferente instead of licitador; descuento
 *    instead of baja; trato directo instead of contrato menor;
 *  - IVA incluido (19 %) instead of «IVA no incluido»; celular, computador;
 *    Chilean places, phone (+56 9 1234 5678) and company forms (SpA, Ltda.);
 *  - «Ingresa» for typing data, as Chile says it.
 * Neutral Spanish that reads fine in Chile stays as `es.ts` has it.
 *
 * Every key must exist in `es.ts` (or `es.no-telegram.ts`) and use only the
 * `{{tokens}}` its es value uses, plus the always-merged ones
 * (`es-cl-overrides.test.ts`). The product is the Spanish one: no line here
 * adds a feature or a legal claim.
 */
export const esCl: Record<string, string> = {
  // ── Landing / catalogue ────────────────────────────────────────
  'landing.hero.description':
    'Consulta las licitaciones públicas de todo Chile. Filtra por región, categoría y estado.',
  // Feed watchdog notice: the source is Mercado Público, not PLACSP.
  'catalog.sourceStale':
    'Mercado Público no publica licitaciones nuevas desde el {{date}}. Mostramos lo último disponible.',
  'catalog.ikpuLabel': 'Código UNSPSC',
  'catalog.budgetFrom': 'Presupuesto desde ($)',
  'catalog.budgetTo': 'Presupuesto hasta ($)',
  // ── Lot page ───────────────────────────────────────────────────
  'tender.seoDesc':
    'Licitación «{{title}}»: compra pública vigente en Chile. Presupuesto, plazo de presentación, requisitos y contacto del organismo comprador en {{brand}}.',
  'tender.seoDescDefault':
    'Licitaciones públicas de Chile: presupuestos, plazos de presentación y contactos de los organismos compradores en {{brand}}.',
  'tender.facts.lead': 'Licitación pública «{{title}}» en Chile.',
  'tender.facts.region': 'Región: {{region}}.',
  'tender.facts.budget': 'Monto estimado: $ {{budget}}.',
  'tender.facts.docs': 'La licitación incluye bases y documentos ({{count}} archivo(s)).',
  'support.companyData.topic': 'Asunto: datos de la empresa, RUT {{tin}}',
  'support.companyData.prefill': 'Datos de la empresa con RUT {{tin}}. ',
  'tender.buyer.profileLink': 'Perfil del organismo comprador y todas sus licitaciones →',
  'tender.outcome.finalPrice': 'Monto adjudicado',
  'tender.outcome.locked': 'Ya está adjudicada. Adjudicatario, monto y contacto, en Pro',
  'tender.outcome.lockedPriceOnly': 'Ya está adjudicada. Adjudicatario y monto, en Pro',
  'tender.outcome.lockedRow': 'Adjudicatario, monto y contacto, en Pro',
  'tender.outcome.lockedRowPrice': 'Adjudicatario y monto, en Pro',
  'tender.contactDetailsAuthed': 'Ábrelo para ver el contacto del organismo comprador.',
  'tender.scanNotice':
    'Texto reconocido a partir de un documento escaneado. Revisa montos, plazos, direcciones y datos con el {{original}}.',
  // No «certificado digital»: that is how one bids in Spain, not in Mercado Público.
  'tender.participateHint': 'Cómo presentar tu oferta en Mercado Público',
  'tender.documentsLabel': 'Bases y documentos',
  'tender.docsTeaser.tz': 'Bases técnicas',
  'tender.docsTeaser.contract': 'Bases administrativas',
  'tender.docsTeaser.cta': 'Desbloquear las bases',
  'tender.docsTeaser.meteredHint':
    'Los archivos están en la plataforma de origen. Al abrirla tienes el enlace al original y el contacto del organismo comprador: lo mismo que el botón de abajo.',
  'tender.procedure.positionsHint':
    'Cuántos bienes, obras o servicios distintos incluye este lote. El organismo comprador los compra en una sola licitación y la oferta se presenta al lote completo.',
  'tender.procedure.announcementNumber': 'ID de la licitación',
  'tender.procedure.representative': 'Representante del organismo comprador',
  'tender.procedure.commission': 'Comisión evaluadora',
  'tender.nicheRequired': 'Los contactos de los organismos compradores están en el plan Pro.',
  'tender.proRequired':
    'Pásate a Pro para ver el contacto del organismo comprador de cada licitación.',
  // ── Region and category hubs (prerendered) ─────────────────────
  'region.seoDesc':
    'Licitaciones públicas vigentes en {{region}}: {{count}} abiertas. Presupuestos, plazos de presentación y contactos de los organismos compradores en {{brand}}.',
  'region.intro':
    'Licitaciones y compras públicas en {{region}}: ahora hay {{count}} abiertas. De cada una tienes el presupuesto, el plazo de presentación y un resumen breve. Filtra por categoría, guarda la búsqueda y recibe las nuevas licitaciones por email.',
  'category.seoDesc':
    'Licitaciones públicas de «{{category}}» en Chile: {{count}} abiertas. Presupuestos, plazos y compradores en {{brand}}.',
  'category.seoDescPaged':
    'Licitaciones de «{{category}}» en Chile, página {{page}} de {{totalPages}}: {{count}} abiertas en total. Presupuestos, plazos y compradores en {{brand}}.',
  'category.intro':
    '«{{category}}» es uno de los sectores de las compras públicas en Chile. Ahora el catálogo tiene {{count}} licitaciones vigentes de esta categoría: de cada una se indica el presupuesto, el plazo de presentación y un resumen breve. Elige la región en el catálogo, guarda la búsqueda y recibe las nuevas licitaciones por email.',
  // ── Sign-up and log-in ─────────────────────────────────────────
  'login.codeTitle': 'Ingresa el código',
  'register.phonePlaceholder': '+56 9 1234 5678',
  'register.phoneInvalid': 'Ingresa un número válido, por ejemplo +56 9 1234 5678',
  'register.emailRequired': 'Ingresa tu email',
  'register.codeIncomplete': 'Ingresa el código de 6 dígitos',
  'auth.error.emailSyntax':
    'Revisa la dirección de email: parece que tiene un error (por ejemplo, falta .com o .cl)',
  'onboarding.companyCodes.offer':
    'Agrega a tu alerta los códigos UNSPSC de tus contratos anteriores: {{names}}.',
  'auth.error.passwordRequired': 'Ingresa la contraseña',
  // A typo is the usual cause: say so first. Asking for a new code is the second option —
  // a new one replaces the code the reader already has (phone QA 26.10).
  'auth.error.invalidCode':
    'El código no es correcto. Revisa que lo hayas escrito tal como viene en el último email que te enviamos. Si pasaron más de 10 minutos, pide un código nuevo.',
  // ── SEO ────────────────────────────────────────────────────────
  'seo.landing.description': 'Consulta las licitaciones públicas de todo Chile.',
  'seo.catalog.title': 'Licitaciones y compras públicas en Chile',
  'seo.catalog.description':
    'Catálogo de licitaciones públicas de Chile: compras vigentes, resúmenes breves, filtros por región y categoría, y alertas por email, en {{brand}}.',
  'seo.catalog.titlePaged': 'Licitaciones públicas en Chile, página {{page}}',
  'seo.results.title': 'Adjudicaciones de compras públicas en Chile: adjudicatarios y montos',
  'seo.results.titlePaged': 'Adjudicaciones en Chile, página {{page}}',
  'seo.results.description':
    'Licitaciones adjudicadas en Chile. Adjudicatario y monto, en Pro; filtros y búsquedas guardadas, gratis.',
  // ── /results ───────────────────────────────────────────────────
  'results.card.inputLabel': 'Palabras, categorías y códigos UNSPSC',
  'results.card.ikpu': 'UNSPSC',
  'results.refine.empty': 'región · monto · exclusiones',
  'results.refine.region': 'Región',
  'results.refine.addRegion': '+ región',
  'results.refine.sum': 'Monto del lote, $',
  'results.kind.goods': 'Bienes',
  'results.change.region': 'región',
  'results.change.budget': 'monto',
  'results.change.ikpu': 'códigos UNSPSC',
  'results.wall.line': 'Adjudicatarios, RUT y montos, en Pro',
  // ── App shell ──────────────────────────────────────────────────
  'shell.section.regions': 'Regiones',
  // ── Saved searches ─────────────────────────────────────────────
  'savedSearches.instant.no.broad':
    'Solo para búsquedas acotadas (palabra, UNSPSC, región, sector…)',
  // Chilean magnitudes: a licitación runs from a few million pesos up.
  'saved.minBudgetLabel': 'Presupuesto desde, $',
  'saved.minBudgetPlaceholder': '5.000.000',
  'saved.maxBudgetLabel': 'Presupuesto hasta, $',
  'saved.maxBudgetPlaceholder': '100.000.000',
  'saved.edit.errorSubject': 'Deja al menos uno: categoría, UNSPSC, sector, comprador o palabra.',
  'saved.emptyCriteria':
    'Agrega al menos un filtro: categoría, región, palabra clave o presupuesto.',
  // ── Lot row / list ─────────────────────────────────────────────
  'tender.budget.unit': '$',
  'tender.budget.expedienteTotal': 'Monto total de la licitación',
  // Chile's name for the bid bond (Ley 19.886 and its Reglamento).
  'tender.bidSecurity': 'Garantía de seriedad de la oferta',
  'tender.consultationNote':
    'Es una consulta al mercado: el organismo comprador pregunta a las empresas antes de llamar a licitación. Responder es gratis y no te compromete, y tus aportes pueden dar forma a las bases.',
  'landing.lotCard.docsLabel': 'Bases (administrativas y técnicas)',
  'tender.notice.consultation': 'Consulta al mercado',
  'org.hint.supplier': '{{name}}: proveedor',
  'catalog.cpvCodeMatch': 'Licitaciones con UNSPSC {{codes}} y todos sus subcódigos',
  'ifi.seo.title': 'Licitaciones del Banco Mundial, el BAsD y la ONU en Chile',
  'ifi.seo.description':
    'Licitaciones abiertas de proyectos de instituciones financieras internacionales en Chile: Banco Mundial, bancos de desarrollo y agencias de la ONU. Plazos, documentos y requisitos.',
  // ── Filter bar ─────────────────────────────────────────────────
  'filter.region': 'Región',
  'filter.ikpu.label': 'O por código UNSPSC',
  'result.hint.gov':
    'Solo se muestran las licitaciones públicas abiertas de Chile: vigentes y a punto de cerrar. Las cerradas y vencidas no cuentan. El catálogo se actualiza automáticamente.',
  'result.hint.ifi':
    'Proyectos financiados por instituciones internacionales (Banco Mundial, BAsD, etc.). No todos publican el presupuesto en pesos, por eso el recuento incluye anuncios abiertos sin monto.',
  // ── Plans and checkout ─────────────────────────────────────────
  'plans.seoTitle': 'Planes y precios: contactos de compradores y bases',
  'plans.seoDesc':
    'Planes de {{brand}}: Pro con los contactos de los organismos compradores, las bases, los adjudicatarios y montos, alertas ilimitadas y exportación a Excel de las compras públicas en Chile. Catálogo, resúmenes y requisitos, gratis.',
  'plans.pageLead':
    'Leer y entender licitaciones es gratis. Pro te dice quién gana, por cuánto y contra quién compites, con alertas sin límite: lo que necesitas para decidir tu oferta. También abre el contacto del organismo comprador y las bases.',
  'plans.freeLimits':
    'Gratis y sin límite de tiempo: el catálogo con el resumen IA y los requisitos de cada licitación, y hasta 3 búsquedas guardadas con alertas por email. Los contactos de los organismos compradores y las bases, solo con Pro.',
  'billing.card.pitchCategory':
    '{{count}} licitaciones de tu categoría cierran esta semana: contactos y bases, dentro',
  'billing.card.pitchMarket': 'Ahora hay {{count}} licitaciones abiertas por $ {{sum}}',
  'plans.payOnceLead': 'El pago se hace en Stripe · no ingresas la tarjeta aquí',
  'plans.payOnceLeadVia': 'El pago se hace en {{gateway}} · no ingresas la tarjeta aquí',
  // Prices are quoted IVA INCLUIDO on Chile (`payments.vatIncludedRate`).
  'billing.stripe.termsHeadline': 'IVA incluido · 14 días de garantía de devolución',
  // Chile asks for RUT + razón social only (`checkoutCollects.billingAddress` false). In Chile
  // «factura» names the SII's electronic tax document, which a US seller's Stripe receipt is not:
  // every card-checkout string says «comprobante de pago» instead.
  'billing.invoice.ask':
    'Para que tu comprobante de pago lleve tu RUT y razón social, agrégalos aquí',
  'billing.stripe.billingTitle': 'Datos para el comprobante',
  'billing.stripe.billingHint':
    'Opcional. Si los agregas, aparecerán en tus comprobantes de pago. También puedes agregarlos después, en Ajustes.',
  'billing.stripe.invoiceToggle': 'Datos para el comprobante (opcional)',
  'billing.invoice.hint': 'Aparecerán en los comprobantes de pago que se emitan desde ahora.',
  'billing.invoice.onFile': 'Datos para el comprobante guardados: {{details}}',
  'billing.invoice.saved':
    'Guardado. Saldrán en tus próximos comprobantes. Si necesitas que el comprobante de este pago los lleve, escríbenos a {{brandEmail}}.',
  'billing.stripe.errorBilling': 'Revisa los datos para el comprobante.',
  'settings.account.attachEmail.hint':
    'No has indicado email: no tenemos dónde enviarte comprobantes ni códigos de acceso',
  'billing.stripe.fieldName': 'Razón social o nombre completo',
  'billing.stripe.fieldTaxId': 'RUT',
  'billing.stripe.fieldLine1Placeholder': 'Calle, número, depto.',
  'billing.stripe.fieldState': 'Región',
  'billing.stripe.errorTaxId': 'Este RUT no es válido: revisa el dígito verificador.',
  'billing.stripe.errorPostalCode': 'Escribe un código postal chileno de 7 dígitos.',
  // Spain's «no IVA» lines, said for a price that includes it. Not rendered on Chile
  // (`vatNotCharged` is off there), but no Spanish tax rule may sit in its catalogue.
  'plans.vat.notCharged': '· IVA incluido',
  'plans.vat.why':
    'Precio con IVA incluido. {{brand}} es un servicio de AI CRM Inc. (EE. UU.); el pago con tarjeta lo procesa Stripe.',
  'plans.cardOnlyPrice': '{{amount}} / mes · IVA incluido',
  'plans.onceExternalHint': 'El pago se abrirá en el navegador del celular',
  'plans.handoffStuck': '¿No se abrió? Abre el enlace en el navegador del celular',
  'plans.social': 'Licitaciones públicas para empresas de todo Chile',
  'plans.socialLive': 'Más de {{count}} empresas de todo Chile ya usan {{brand}}',
  'plans.octo.redirectNote': 'Los datos de la tarjeta se ingresan en la página segura de Stripe',
  'billing.card.otpUseLast': 'Ingresa el último código que recibiste',
  'billing.card.otpSubtitle': 'Ingresa el código de confirmación de tu banco.',
  'billing.card.errorTinRequired':
    'Este plan requiere el RUT de la empresa. Agrégalo en los ajustes y repite el pago: el código de confirmación no tiene nada que ver.',
  'billing.card.errorEmpty': 'Ingresa el número de la tarjeta',
  'billing.card.errorIncomplete': 'Ingresa los 16 dígitos de la tarjeta',
  'plans.som': '$',
  'plans.feature.categoryLimitUnlimited': 'Categorías UNSPSC ilimitadas',
  'plans.feature.categoryNiche': '1 sector UNSPSC + alertas por email',
  // The two Mercado Público sources (`mp-licitaciones`, `mp-compra-agil`).
  'plans.feature.allTenders':
    'Todas las licitaciones públicas de Chile (Mercado Público: licitaciones y Compra Ágil)',
  'plans.feature.privateTenders':
    'Todo el sector público en Mercado Público: servicios del Estado, municipalidades y otros organismos',
  'plans.feature.ifi': 'Adjudicatario y monto de cada licitación',
  'plans.feature.budgetCeiling':
    'Licitaciones con presupuesto de hasta {{amount}} millones de pesos',
  'plans.feature.contactsAll': 'Contactos de los organismos compradores: email y teléfono',
  'plans.feature.documents':
    'Bases y documentos de cada licitación (administrativas, técnicas y anexos)',
  'plans.feature.aiSummary': 'Resumen IA y requisitos de las bases, gratis',
  'plans.feature.aiSummaryFull': 'Resumen IA completo y requisitos de las bases',
  'plans.feature.savedSearchOne': 'Hasta 3 búsquedas guardadas + alertas por email',
  'plans.feature.analytics': 'Descuento y monto adjudicado en licitaciones parecidas',
  'plans.feature.sourceAlso':
    'Contacto del organismo comprador, bases y publicación oficial en un clic',
  // ── Market figures on the lot («baja» is Spain's word for the discount) ──
  'baseRates.hint':
    'Descuento mediano sobre el presupuesto estimado, número de oferentes y rango del monto adjudicado en licitaciones cerradas de la misma categoría y tamaño parecido (de 0,3 a 3 veces el presupuesto de este lote). Te ayuda a evaluar la competencia y un precio justo.',
  'baseRates.competition': 'Oferentes (mediana)',
  'baseRates.discount': 'Descuento mediano',
  'baseRates.discountCaption':
    'Cuánto descontaron los adjudicatarios sobre el presupuesto estimado',
  'baseRates.priceBand': 'Rango del monto adjudicado',
  'baseRates.priceMedian': 'Monto adjudicado (mediana)',
  'baseRates.basisBanded':
    'Según {{n}} licitaciones comparables: monto adjudicado {{min}} – {{max}}, {{from}} – {{to}}',
  'baseRates.basisBandedNoDates':
    'Según {{n}} licitaciones comparables: monto adjudicado {{min}} – {{max}}',
  'baseRates.positionBelow':
    'El presupuesto del lote está por debajo del monto habitual adjudicado en licitaciones así',
  'baseRates.positionInside':
    'El presupuesto del lote está dentro del rango habitual de montos adjudicados',
  'baseRates.positionAbove':
    'El presupuesto del lote está por encima del monto habitual adjudicado en licitaciones así',
  'baseRates.winLine.basis': 'Descuento mediano en {{n}} lotes parecidos:',
  // ── /market ────────────────────────────────────────────────────
  'market.title': 'El mercado de las compras públicas en Chile',
  'market.subtitle':
    'Licitaciones abiertas y adjudicaciones en cifras: presupuestos, regiones, sectores y evolución.',
  'market.summary':
    'Ahora hay {{active}} licitaciones públicas abiertas en Chile por un total de {{budget}}; en la última semana se agregaron {{newWeek}}. Abajo tienes el presupuesto por región y sector, y las adjudicaciones de las licitaciones cerradas.',
  'market.seo.title': 'Compras públicas en Chile: estadísticas y análisis',
  'market.seo.description':
    'Análisis del mercado de compras públicas en Chile: cuántas licitaciones hay abiertas, presupuestos por región y sector, volumen y competencia según las adjudicaciones.',
  'market.som': '$',
  // «Trato directo» is Chile's direct award (Ley 19.886).
  'market.direct':
    'Adjudicaciones sin concurrencia (tratos directos) en el mismo periodo: {{count}} por {{volume}}. No se incluyen en las cifras de arriba.',
  'market.byRegion': 'Presupuesto por región',
  'market.competition': 'Oferentes (mediana) por sector',
  'market.discount': 'Descuento mediano por sector',
  // ── /prices ────────────────────────────────────────────────────
  'prices.subtitle': 'Cuánto paga en promedio el Estado por cada categoría de producto',
  'prices.seoDesc.bandDated':
    'Monto mediano del contrato «{{name}}»: $ {{median}}, rango {{p25}}–{{p75}}. Según {{n}} contratos públicos adjudicados en Chile entre {{from}} y {{to}}.',
  'prices.seoDesc.band':
    'Monto mediano del contrato «{{name}}»: $ {{median}}, rango {{p25}}–{{p75}}. Según {{n}} contratos públicos adjudicados en Chile.',
  'prices.seoDesc.sample':
    'Estadísticas de contratos públicos adjudicados de «{{name}}» en Chile: muestra de {{n}} contratos con monto adjudicado.',
  'prices.seoDesc.empty':
    '¿Cuánto paga el Estado por «{{name}}»? Datos de contratos públicos adjudicados en Chile.',
  'prices.band': 'Monto adjudicado',
  'prices.discount': 'Descuento mediano sobre el presupuesto estimado',
  'prices.basedOn':
    'según {{n}} contratos de la muestra en los que la fuente publica el presupuesto estimado',
  'prices.discountUnknown':
    'sin datos: la fuente registra el presupuesto estimado como monto final ({{n}} contratos)',
  'prices.participants': 'Oferentes (mediana)',
  'prices.caveat': 'Se muestra el monto del contrato por categoría, no el precio por unidad.',
  // ── «Próximos vencimientos» (features.expiringContracts) ───────
  'expiring.intro':
    'Contratos adjudicados cuyo plazo termina pronto. Cuando un contrato vence, el organismo suele volver a licitarlo: así puedes preparar tu oferta antes de que se publique.',
  'expiring.note':
    'Fecha estimada con la adjudicación y la duración que publica la fuente. Es probable, no garantizado: el contrato puede prorrogarse o licitarse de nuevo antes. No incluye contratos de menos de 6 meses.',
  'expiring.seoTitle': 'Próximos vencimientos de contratos públicos en Chile',
  'expiring.seoDesc':
    'Contratos públicos que vencen en los próximos 3, 6 y 12 meses: organismo comprador, objeto, UNSPSC y región. Adelántate a la próxima licitación.',
  'expiring.filter.search': 'Buscar por objeto u organismo',
  'expiring.filter.allRegions': 'Todas las regiones',
  'expiring.filter.allCpv': 'Todos los sectores (UNSPSC)',
  'expiring.filter.buyer': 'Organismo: {{name}}',
  'expiring.filter.clearBuyer': 'Quitar el filtro de organismo',
  'expiring.filterByBuyer': 'Solo este organismo',
  'expiring.locked': 'Adjudicatario y monto, en Pro',
  // ── Likely bidders, buyer and supplier pages ───────────────────
  'similarWinners.title': 'Posibles oferentes',
  'similarWinners.hint':
    'Empresas que obtuvieron contratos con el mismo código UNSPSC en los últimos 12 meses, por licitación o de forma directa. Si con el código exacto hay menos de tres empresas, se usa la clase del código, más amplia.',
  'similarWinners.basisExact': 'Código UNSPSC {{code}}: {{awards}}, {{winners}} en 12 meses',
  'similarWinners.basisClass':
    'Clase UNSPSC {{code}} (con el código exacto hay pocas empresas): {{awards}}, {{winners}} en 12 meses',
  'supplierGraph.hint':
    'Hasta qué punto las compras de este comprador se concentran en pocas empresas y si es fácil entrar como nuevo proveedor. Cuantos menos contratos, más prudente es la conclusión: por debajo de 10 es provisional y por debajo de 5 no la damos, solo las cifras. No se cuentan las adjudicaciones directas sin licitación.',
  'supplierGraph.concentrationHint.high': 'Es difícil entrar como nuevo proveedor',
  'supplierGraph.concentrationHint.low': 'Es fácil entrar como nuevo proveedor',
  'supplierGraph.footer': '{{suppliers}} empresas · {{contracts}} contratos · $ {{total}}',
  'supplierProfile.totalValue': 'Monto de los contratos',
  'supplierProfile.p12.value': 'Monto de los contratos',
  'supplierProfile.p12.discount': 'Descuento mediano sobre el presupuesto estimado',
  'supplierProfile.p12.discountNote': 'según {{n}} contratos con presupuesto estimado',
  'supplierProfile.p12.participants': 'Oferentes (mediana) en los lotes ganados',
  'supplierProfile.p12.bidsNote':
    '{{wins}} adjudicaciones de {{n}} lotes en los que la fuente publica todos los oferentes; no se conoce la tasa total de participación',
  'supplierProfile.regions': 'Regiones',
  'buyer.tin': 'RUT',
  'org.tinNumber': 'RUT {{number}}',
  'buyer.seoTitle': '«{{name}}», RUT {{tin}}: contratos y adjudicatarios',
  'buyer.seoDescription':
    '{{name}}, RUT {{tin}}: {{awards}} contratos por {{value}}, {{suppliers}} empresas adjudicatarias, {{active}} licitaciones abiertas. Categorías y montos en {{brand}}.',
  'supplier.seoTitle': '«{{name}}», RUT {{tin}}: contratos públicos y adjudicaciones',
  'supplier.seoDescription':
    '{{name}}, RUT {{tin}}: {{contracts}} contratos públicos por {{value}}, {{buyers}} compradores, {{period}}. Categorías y regiones en {{brand}}.',
  'buyer.kpi.value': 'Monto de los contratos',
  'buyer.p12.value': 'Monto de los contratos',
  'buyer.p12.discount': 'Descuento mediano sobre el presupuesto estimado',
  'buyer.p12.discountNote': 'según {{n}} lotes con presupuesto estimado',
  'buyer.p12.participants': 'Oferentes (mediana)',
  'buyer.p12.participantsNote': 'según {{n}} lotes con número de oferentes',
  'buyer.p12.singleBidder': 'Lotes con un solo oferente',
  'buyer.topShareSplit':
    'Principal adjudicatario: {{value}} % del monto · {{count}} % de los contratos',
  'buyer.winnerSub':
    '{{wins}} adjudicaciones · {{value}} % del monto / {{count}} % de los contratos',
  'buyer.amountUndisclosed': 'Sin monto publicado',
  // ── Requirements and checkout leftovers ────────────────────────
  'requirements.sourceDocuments': 'según las bases del lote',
  'requirements.awardCriteria': 'Criterios de evaluación',
  // «Solvencia» is Spain's LCSP term. The marketplace list of a Mercado Público lot is its
  // criterios de evaluación (`tender-overview.tsx` heads it with `awardCriteria` on Chile);
  // what is read from the bases is what a bidder must meet to take part.
  'requirements.qualifications': 'Requisitos para participar',
  'requirements.noneStated':
    'Revisamos los documentos del lote: no indican requisitos para participar.',
  'plans.tinRequired': 'Este plan requiere el RUT de la empresa. Agrégalo y vuelve al pago.',
  'plans.bankAmount': 'Monto a pagar',
  // ── Pay by invoice (bankInvoice is off on Chile too; kept Chilean) ──
  'invoiceFlow.tinLabel': 'RUT de tu organización',
  'invoiceFlow.namePlaceholder': 'Ejemplo SpA',
  'invoiceFlow.errIssue': 'No se pudo emitir la factura. Revisa el RUT e inténtalo de nuevo.',
  'plans.savingsX2': 'Un año por el precio de 6 meses: ahorras $ {{som}}',
  'plans.refYearAria': 'Pagando mes a mes, el año saldría en $ {{som}}',
  // ── /directory and /companies ──────────────────────────────────
  'directory.title': 'Todas las categorías y regiones',
  'directory.intro':
    'Índice completo de las secciones del catálogo: licitaciones públicas de Chile por sector y por región del comprador. Junto a cada sección, el número de licitaciones abiertas.',
  'directory.seoTitle': 'Licitaciones por categoría y región en Chile',
  'directory.seoDesc':
    'Índice de todas las secciones del catálogo de compras públicas de Chile: licitaciones por sector y por región, con el número de licitaciones abiertas en cada una.',
  'directory.regions.h2': 'Por región del comprador',
  'directory.regions.intro': 'La región es donde está el comprador, no el lugar de entrega.',
  'directory.companies.h2': 'Empresas en las compras públicas',
  'directory.companies.intro':
    'Catálogo de proveedores y compradores con historial en las compras públicas de Chile: estado en el registro, fiabilidad y volumen de contratos.',
  'companies.title': 'Empresas en las compras públicas de Chile',
  'companies.intro':
    'Proveedores y compradores con historial en las compras públicas. Junto a cada uno, su estado en el registro y el volumen de contratos.',
  'companies.seoTitle': 'Catálogo de empresas de las compras públicas en Chile',
  'companies.seoTitlePaged': 'Empresas de las compras públicas, página {{page}}',
  'companies.seoDescPaged':
    'Empresas de las compras públicas en Chile, página {{page}} de {{totalPages}}, {{count}} en total. Compradores y adjudicatarios, contratos y montos en {{brand}}.',
  'companies.seoDesc':
    'Empresas adjudicatarias y compradores de las compras públicas en Chile: estado en el registro, fiabilidad e historial de contratos de cada una.',
  'companies.search': 'Nombre o RUT',
  'companies.filter.allRegions': 'Todas las regiones',
  'footer.directory': 'Categorías y regiones',
  // ── /about ─────────────────────────────────────────────────────
  'about.subtitle': 'Agregador de licitaciones y compras públicas de Chile.',
  'about.lead':
    '{{brand}} es el catálogo de las compras públicas en Chile. Reunimos en un solo lugar las licitaciones publicadas en Mercado Público, agregamos un análisis de cada una y ayudamos a las empresas a encontrar antes las compras públicas que les sirven.',
  // One official platform in Chile, not «decenas de plataformas».
  'about.what.body':
    'Cada pocas horas actualizamos el catálogo de licitaciones públicas, identificamos la región, la categoría y el objeto de cada una, y las hacemos fáciles de buscar: así no tienes que revisar Mercado Público a mano todos los días.',
  'about.feature.catalog.desc':
    'Miles de licitaciones vigentes con filtros por región, categoría, presupuesto y plazo de presentación.',
  'about.feature.ai.desc':
    'Un resumen breve de cada licitación: entiendes de qué se trata al momento.',
  'about.audience.body':
    'Para empresas, emprendedores, contratistas y fabricantes que participan en las compras públicas de Chile y quieren ver antes que nadie las licitaciones que les interesan.',
  'about.sources.body':
    'Agregamos los datos abiertos de Mercado Público, la plataforma de compras públicas del Estado de Chile que administra la Dirección ChileCompra. {{brand}} es un servicio independiente: no está vinculado a ChileCompra ni es un portal del Estado.',
  // ── Printable invoice ──────────────────────────────────────────
  'invoice.buyerInn': 'RUT',
  'invoice.itemPrice': 'Monto',
  'invoice.note':
    'Transfiere el monto indicado a la cuenta del proveedor. El plan {{name}} se activa cuando recibimos el pago. Concepto: suscripción {{name}}, {{brandHost}}.',
  // ── Lot page extras ────────────────────────────────────────────
  'tender.lotDetails.batchTotal': 'Monto total de la licitación',
  'tenderIntegrity.hint':
    'Señales a tener en cuenta: nuevo llamado, oferente único, garantía, requisitos. Solo hechos, sin valoraciones.',
  'tenderIntegrity.teaserRelisting': 'Nuevo llamado del lote',
  'tenderIntegrity.relisting': 'Nuevo llamado del procedimiento #{{id}}',
  'tenderIntegrity.prevParticipants': 'oferentes en él: {{n}}',
  'tenderIntegrity.participants': 'Oferentes registrados: {{n}}',
  'tenderIntegrity.single': 'Único oferente registrado',
  'tender.overview.bidSecurity': 'Garantía de seriedad de la oferta',
  'tender.documentTextLabel': 'Texto de las bases',
  'tender.reveal.noContactHint':
    'El organismo comprador no publicó contacto para esta licitación. Al abrirla tienes el enlace a la publicación original en la plataforma.',
  // ── Bid package (behind BID_PACKAGE_ENABLED) ───────────────────
  'tender.bidPackage.noCompany':
    'No indicaste el RUT en tu perfil, así que no pudimos comprobar los requisitos con tu empresa: tendrás que revisarlos todos por tu cuenta.',
  'tender.bidPackage.groupAutoHint':
    'Confirmado con el registro y el historial de adjudicaciones. No reemplaza la revisión de la comisión evaluadora.',
  'tender.bidPackage.groupManualHint':
    'Requisitos obligatorios que solo tú puedes confirmar: certificados, declaraciones juradas, copias autorizadas.',
  'tender.bidPackage.basis.region': 'Región de la empresa: {{value}}',
  'tender.bidPackage.basis.vat': 'Contribuyente de IVA: {{value}}',
  'tender.bidPackage.disclaimer':
    'Borrador preparado automáticamente a partir de la publicación y los archivos adjuntos. Antes de presentarlo, compáralo con el original: el oferente es responsable del contenido de su oferta.',
  // ── Bid documentation waitlist ─────────────────────────────────
  'tender.bidWaitlistForm.termsLead':
    'Si ganas con nuestra documentación, firmamos un contrato y cobramos una comisión sobre el monto adjudicado:',
  // Placeholder bands in pesos (as Spain's euro ones were) until the owner sets Chile's.
  'tender.bidWaitlistForm.tier1Range': 'hasta $ 100 millones',
  'tender.bidWaitlistForm.tier2Range': 'hasta $ 1.000 millones',
  'tender.bidWaitlistForm.tier3Range': 'más de $ 1.000 millones',
  'tender.bidWaitlistForm.supplyPlaceholder':
    'mobiliario, artículos de oficina, reparación de techumbres',
  'tender.bidWaitlistForm.lotLabel': 'Monto habitual del lote',
  'tender.bidWaitlistForm.lot.to_100m': 'hasta $ 10 millones',
  'tender.bidWaitlistForm.lot.to_1b': 'hasta $ 100 millones',
  'tender.bidWaitlistForm.lot.to_10b': 'hasta $ 1.000 millones',
  'tender.bidWaitlistForm.lot.over_10b': 'más de $ 1.000 millones',
  // ── Lot Pro wall ───────────────────────────────────────────────
  'tender.gate.lockedContacts': 'Contacto del organismo comprador',
  'tender.gate.lockedDocs': 'Bases y documentos',
  'tender.gate.lockedSummary': 'Resumen completo de las bases',
  'tender.gate.free':
    'Hazte Pro: quién gana, por cuánto y contra quién compites, y alertas sin límite. También el contacto del organismo comprador y las bases.',
  'freeUnlock.needTin': 'Agrega el RUT de la empresa',
  // ── Onboarding ─────────────────────────────────────────────────
  'ikpu.picker.placeholder': 'Busca por nombre de producto o código UNSPSC…',
  'onboarding.ikpu.label': 'O por código UNSPSC',
  // Sample lots in the words a Mercado Público title uses.
  'onboarding.sample.tender.automotive': '«Adquisición de vehículos para el servicio municipal»',
  'onboarding.sample.tender.food': '«Suministro de alimentos para un jardín infantil»',
  'onboarding.sample.tender.consulting': '«Servicio de inspección técnica de obras (ITO)»',
  'onboarding.sample.tender.electrical': '«Suministro e instalación de un transformador eléctrico»',
  'onboarding.sample.tender.energy-fuel': '«Suministro de petróleo diésel»',
  'onboarding.sample.tender.office-supplies': '«Adquisición de artículos de oficina»',
  'onboarding.sample.tender.cleaning-sanitation': '«Servicio de aseo de edificios institucionales»',
  'onboarding.sample.meta': '💰 $ 24.000.000 · ⏰ quedan 12 días',
  'onboarding.sample.budget': '💰 $ {{amount}}',
  // ── Dashboard, team ────────────────────────────────────────────
  'dashboard.plan.free':
    'Plan gratuito: pásate a Pro para abrir los contactos, las bases y la exportación.',
  'join.teamPlan':
    'Plan del equipo: {{plan}}. Tendrás los contactos de los compradores, las bases y la analítica',
  // ── Kind tabs ──────────────────────────────────────────────────
  'catalog.kind.goods': 'Bienes',
  // ── Company step ───────────────────────────────────────────────
  'company.benefit.plan': 'Te ayudamos a no perderte las licitaciones de tu región y tu sector',
  'company.card.region': 'Región:',
  'company.card.notFound':
    'No encontramos ninguna empresa con ese RUT. Revisa el número o, si es correcto, déjalo así: puede que aún no tengamos sus datos.',
  // ── RUT wording: «N caracteres» is Spain's fixed nine; a RUT is a number and its
  // dígito verificador (12345678-5 is the usual dummy). ──────────
  'register.tinHint':
    'Con dígito verificador: te mostraremos las licitaciones que has ganado (opcional)',
  'register.tinInvalid': 'Revisa el {{tin}}: número y dígito verificador, por ejemplo 12345678-5',
  'onboarding.inn.digitsHint': 'Número y dígito verificador, por ejemplo 12345678-5',
  'invoiceFlow.tinIncomplete': 'Completa el {{tin}}: número y dígito verificador',
  'tender.bidWaitlistForm.tinPlaceholder': 'ej.: 12345678-5',
  'tender.bidWaitlistForm.tinMissing': 'Indica el {{tin}} de la empresa (ej.: 12345678-5)',
  // ── Settings and the company step ──────────────────────────────
  'settings.summary.noCompany': 'RUT sin indicar',
  'settings.account.attachEmail.label':
    'Ingresa tu email y te enviaremos un código de confirmación',
  'settings.account.attachEmail.codeLabel': 'Código enviado a {{email}}. Ingrésalo:',
  'company.tinLabel': 'RUT',
  'company.tinPlaceholder': 'ej.: 12345678-5',
  'company.lookup.label': 'Ingresa el {{tin}} o el nombre de tu organización',
  'company.lookup.placeholder': 'Nombre o {{tin}} (ej.: 12345678-5)',
  'company.saved': 'RUT guardado.',
  'company.foundRegion': 'En tu región hay ahora {{n}} {{word}} en plazo',
  'company.error.invalidTin': 'Revisa el RUT: escribe el número y el dígito verificador',
  'company.error.invalidControl': 'Revisa el RUT: el dígito verificador no coincide',
  'company.error.changeLimit': 'Hoy cambiaste el RUT demasiadas veces. Inténtalo mañana.',
  'claim.card.needsTin': 'Primero indica el RUT de la empresa en la sección de arriba.',
  'claim.conflict.title': 'Este RUT ya está vinculado a otra cuenta',
  // ── Saved-search editor ────────────────────────────────────────
  'saved.categoryAdd': 'Agregar categoría, UNSPSC o sector',
  'saved.ikpuLabel': 'Códigos UNSPSC de la búsqueda',
  'saved.ikpuRemove': 'Quitar el código UNSPSC',
  'saved.regionLabel': 'Regiones',
  'saved.textHintCodes':
    'Con códigos UNSPSC elegidos, las palabras no filtran: te llegan todos los lotes de esos códigos.',
  'saved.anyRegion': 'Todas las regiones',
  // ── AI onboarding ──────────────────────────────────────────────
  'aiOnboarding.step1.desc':
    'Qué haces, dónde trabajas y desde qué monto, con tus palabras. Con eso elegimos los códigos UNSPSC, las regiones y las palabras de tus alertas.',
  'aiOnboarding.step1.placeholder':
    'Ej.: aseo de oficinas y colegios en Santiago y Valparaíso, contratos desde $ 5.000.000',
  'aiOnboarding.step1.example':
    'Otros ejemplos: «mantención de alumbrado público en la Región del Biobío», «artículos de oficina».',
  'aiOnboarding.step1.nifHint': 'El RUT se pide en el paso siguiente.',
  'aiOnboarding.step1.nifGo': 'Ir al paso del RUT',
  'aiOnboarding.step1.nifNoStep': 'Aquí no hace falta el RUT: describe qué ofreces.',
  'aiOnboarding.row.subject': 'Categorías, UNSPSC y sectores',
  'aiOnboarding.row.ikpu': 'Códigos UNSPSC',
  'aiOnboarding.row.regions': 'Regiones',
  'aiOnboarding.broad.desc':
    'Las alertas incluirían casi todo el catálogo. Agrega una palabra o una región para afinar.',
  'aiOnboarding.widened.class':
    'códigos ampliados a su clase UNSPSC: llegarán también partidas cercanas del mismo producto',
  'aiOnboarding.widened.regions': 'en todo Chile, sin región',
  'aiOnboarding.fill.placeholder':
    'necesito bombas y motores eléctricos, Santiago y alrededores, desde $ 5.000.000',
  // ── Pay from a computer ────────────────────────────────────────
  'plans.payFromDesktop': 'Pagar desde el computador',
  'payLink.title': 'Pago desde el computador',
  'plans.sms.err.invalid_phone': 'Ingresa el número: +56 y 9 dígitos',
  // ── /zayavka (mounted on UZ only; kept Chilean so nothing Spanish is left) ──
  'zayavka.s1.search': 'Producto, servicio o código UNSPSC',
  'zayavka.s1.summary': '{{count}} {{lots}} por $ {{sum}}',
  'zayavka.q.form.legal': 'Sociedad (SpA, Ltda., S.A.…)',
  'zayavka.q.form.ip': 'Persona natural con giro',
  'zayavka.q.budget.lt_100m': 'Hasta $ 10 millones',
  'zayavka.q.budget.100m_1b': '$ 10 – 100 millones',
  'zayavka.q.budget.1b_10b': '$ 100 – 1.000 millones',
  'zayavka.q.budget.gt_10b': 'Más de $ 1.000 millones',
  'zayavka.id.label': 'RUT',
  'zayavka.id.invalid': 'Revisa el RUT: número y dígito verificador',
};
